/**
 * Image URL utilities for handling images across different environments
 * - Local development: Express server
 * - Preview/Production: Cloudflare Pages Functions + R2
 */

import { resolveSiteUrl } from "@shared/config";

/**
 * Get current site URL from browser or environment
 */
function getCurrentSiteUrl(env?: { SITE_URL?: string }): string {
  // In browser, use window.location.origin
  if (typeof window !== "undefined" && window.location) {
    return window.location.origin;
  }
  // Fallback to resolveSiteUrl
  return resolveSiteUrl(env);
}

/**
 * Check if URL is from R2 bucket
 */
function isR2Url(url: string): boolean {
  if (!url) return false;
  // R2 bucket URLs typically contain bucket name or R2 domain
  return (
    url.includes("ozknews-media") ||
    url.includes("r2.dev") ||
    url.includes("/media/") ||
    url.startsWith("r2://")
  );
}

/**
 * Convert R2 object key to public URL
 * R2 objects are served via Cloudflare Pages Functions
 */
function r2KeyToPublicUrl(key: string, env?: { SITE_URL?: string }): string {
  const siteUrl = getCurrentSiteUrl(env);

  // Remove r2:// prefix if present
  let cleanKey = key.replace(/^r2:\/\//, "");

  // If key already contains full URL, extract the key and rebuild with current origin
  if (cleanKey.startsWith("http://") || cleanKey.startsWith("https://")) {
    // Extract key from full URL (e.g., https://domain.com/api/media/key -> key)
    const urlMatch = cleanKey.match(/\/api\/media\/(.+)$/);
    if (urlMatch) {
      // Extract and decode the key (it may be URL-encoded)
      let extractedKey = urlMatch[1];
      try {
        extractedKey = decodeURIComponent(extractedKey);
      } catch {
        // If decoding fails, use as is
      }
      const baseUrl = siteUrl.replace(/\/$/, "");
      // Re-encode properly for the new URL
      const encodedKey = extractedKey
        .split("/")
        .map((segment) => encodeURIComponent(segment))
        .join("/");
      return `${baseUrl}/api/media/${encodedKey}`;
    }
    // If it's a different domain, return as is (external image)
    return cleanKey;
  }

  // If key already starts with /api/media/, just prepend the origin
  if (cleanKey.startsWith("/api/media/")) {
    const baseUrl = siteUrl.replace(/\/$/, "");
    return `${baseUrl}${cleanKey}`;
  }

  // Construct public URL via Pages Functions
  // R2 images are served via /api/media/[...path] endpoint
  // The path parameter is a catch-all, so we need to encode the key properly
  const baseUrl = siteUrl.replace(/\/$/, "");
  // URL encode each segment of the path
  // First decode if already encoded to avoid double encoding
  try {
    cleanKey = decodeURIComponent(cleanKey);
  } catch {
    // If decoding fails, assume it's not encoded
  }
  const encodedKey = cleanKey
    .split("/")
    .map((segment) => encodeURIComponent(segment))
    .join("/");
  return `${baseUrl}/api/media/${encodedKey}`;
}

/**
 * Normalize image URL for current environment
 * - Converts HTTP to HTTPS
 * - Handles R2 bucket URLs
 * - Preserves external URLs (Unsplash, Pexels, etc.)
 */
export function normalizeImageUrl(
  url: string | null | undefined,
  env?: { SITE_URL?: string }
): string | null {
  // 안전한 null/undefined 체크
  if (!url || typeof url !== "string") return null;

  // Data URLs, blob URLs, etc. - return as is
  if (url.startsWith("data:") || url.startsWith("blob:")) {
    return url;
  }

  // Debug logging (only in development)
  if (typeof window !== "undefined" && process.env.NODE_ENV === "development") {
    console.log("[normalizeImageUrl] Input URL:", url);
  }

  // Already normalized HTTPS URLs
  if (url.startsWith("https://")) {
    // Check if it's an R2 URL that needs conversion
    if (isR2Url(url)) {
      // Extract key from URL if it's a full R2 URL
      const match = url.match(/ozknews-media[^/]*\/(.+)$/);
      if (match) {
        return r2KeyToPublicUrl(match[1], env);
      }
    }
    // If it contains /api/media/, extract the key and rebuild with current origin
    // This handles cases where URL was saved with a different domain (e.g., preview vs production)
    if (url.includes("/api/media/")) {
      const urlMatch = url.match(/\/api\/media\/(.+)$/);
      if (urlMatch) {
        const extractedKey = urlMatch[1];
        const siteUrl = getCurrentSiteUrl(env);
        const baseUrl = siteUrl.replace(/\/$/, "");
        return `${baseUrl}/api/media/${extractedKey}`;
      }
    }
    // If it's an external HTTPS URL (Unsplash, Pexels, etc.), return as is
    return url;
  }

  // Convert HTTP to HTTPS
  if (url.startsWith("http://")) {
    return url.replace(/^http:\/\//, "https://");
  }

  // Handle relative URLs starting with /api/media/ (already normalized R2 URLs)
  if (url.startsWith("/api/media/")) {
    const siteUrl = getCurrentSiteUrl(env);
    const baseUrl = siteUrl.replace(/\/$/, "");
    // Extract the key part (everything after /api/media/)
    const keyPart = url.slice("/api/media/".length);
    if (keyPart) {
      // The key part from DB should be the actual R2 key (e.g., "articles/2025-11-08/file.jpg")
      // It might be URL-encoded or not, so we decode it first to get the actual key
      let actualKey: string;
      try {
        // Try to decode - if it fails, it's probably not encoded
        actualKey = decodeURIComponent(keyPart);
      } catch {
        actualKey = keyPart;
      }

      // Now encode each segment of the path properly for the URL
      // This ensures the URL works correctly when sent to the server
      const encodedKey = actualKey
        .split("/")
        .map((segment) => {
          // Each segment should be encoded separately
          return encodeURIComponent(segment);
        })
        .join("/");

      const result = `${baseUrl}/api/media/${encodedKey}`;

      // Debug logging (only in development)
      if (typeof window !== "undefined" && process.env.NODE_ENV === "development") {
        console.log("[normalizeImageUrl] /api/media/ URL transformation:", {
          original: url,
          keyPart,
          actualKey,
          encodedKey,
          result,
          siteUrl,
        });
      }

      return result;
    }
    return `${baseUrl}${url}`;
  }

  // Handle R2 object keys (r2://key or just key)
  if (isR2Url(url) || url.startsWith("r2://")) {
    return r2KeyToPublicUrl(url, env);
  }

  // Relative URLs - assume they're from the same origin
  if (url.startsWith("/")) {
    const siteUrl = getCurrentSiteUrl(env);
    const baseUrl = siteUrl.replace(/\/$/, "");
    return `${baseUrl}${url}`;
  }

  // Default: assume it's an external URL and convert to HTTPS
  const result = url.startsWith("//") ? `https:${url}` : `https://${url}`;

  // Debug logging (only in development)
  if (typeof window !== "undefined" && process.env.NODE_ENV === "development") {
    console.log("[normalizeImageUrl] Output URL:", result);
  }

  return result;
}

/**
 * Legacy function for backward compatibility
 * @deprecated Use normalizeImageUrl instead
 */
export function httpsOnly(url: string | null | undefined): string | null {
  return normalizeImageUrl(url);
}
